import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { DatePart } from '../interfaces/date-part';

export type { DatePart } from '../interfaces/date-part';

function resolveTwoDigitYear(twoDigitYear: number): number {
  const currentYear = new Date().getFullYear();
  const century = Math.floor(currentYear / 100) * 100;
  const fullYear = century + twoDigitYear;
  // Century ambiguity: if the resolved year is still in the future, assume 1900s.
  return fullYear > currentYear ? fullYear - 100 : fullYear;
}

/** Parses the YYMMDD date-of-birth portion embedded in a South African ID number. */
export function parseDobPartFromId(dobPart: string): DatePart | null {
  if (!/^\d{6}$/.test(dobPart)) {
    return null;
  }

  const yy = Number(dobPart.slice(0, 2));
  const mm = Number(dobPart.slice(2, 4));
  const dd = Number(dobPart.slice(4, 6));
  const year = resolveTwoDigitYear(yy);

  const date = new Date(Date.UTC(year, mm - 1, dd));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== mm - 1 || date.getUTCDate() !== dd) {
    return null;
  }

  return { year, month: mm, day: dd };
}

/**
 * Validates a 13-digit South African ID number:
 * - 13 numeric digits
 * - Digits 1-6 form a valid YYMMDD date of birth
 * - Digit 13 is a Luhn checksum digit
 */
export function isValidSaIdNumber(value: string): boolean {
  const id = value.trim();
  if (!/^\d{13}$/.test(id)) {
    return false;
  }

  if (!parseDobPartFromId(id.substring(0, 6))) {
    return false;
  }

  let sum = 0;
  let alternate = false;

  for (let i = id.length - 1; i >= 0; i--) {
    let digit = id.charCodeAt(i) - 48;
    if (alternate) {
      digit *= 2;
      if (digit > 9) {
        digit = (digit % 10) + 1;
      }
    }
    sum += digit;
    alternate = !alternate;
  }

  return sum % 10 === 0;
}

export function saIdNumberValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = String(control.value ?? '').trim();
    if (!value) {
      return null; // Required validation handles empty values.
    }
    return isValidSaIdNumber(value) ? null : { invalidSaIdNumber: true };
  };
}
